import type { ILeadQueueRepository } from '@/domain/repositories/ILeadQueueRepository';
import type { DeviceSession } from '@/domain/entities/DeviceSession';
import type { LeadCaptureInput } from '@/domain/entities/LeadCaptureInput';
import { QUEUE_ALERT_THRESHOLD, QUEUE_HARD_LIMIT, createQueuedLead } from '@/domain/entities/QueuedLead';
import type { QueuedLead } from '@/domain/entities/QueuedLead';

export type EnqueueLeadResult =
  | { outcome: 'queued'; lead: QueuedLead; queueSize: number; showQueueAlert: boolean }
  // CA-09: fila já no hard limit — criação bloqueada, `QueueFullModal` deve
  // ser exibido pela camada de apresentação.
  | { outcome: 'queue-full' }
  // CA-03: IndexedDB indisponível — POST cancelado, sem exposição em UI (o
  // erro estruturado já foi registrado em `console.error` por este use
  // case; quem chama não deve logar de novo).
  | { outcome: 'storage-unavailable' };

/**
 * Grava um lead na `lead_queue` do IndexedDB **antes de qualquer tentativa
 * de rede** (CA-01) e calcula os limiares de alerta (9.500, CA-08) e de
 * bloqueio (10.000, CA-09). Não depende de `fetch`/rede — a sincronização em
 * si é responsabilidade de `SyncLeadQueueUseCase`.
 *
 * `input` foi alargado de `FakeLeadInput` para `LeadCaptureInput` (HUB-237,
 * mudança de tipo retrocompatível — `FakeLeadInput` já satisfaz a interface
 * mais ampla estruturalmente): tanto `CaptureFakeLeadUseCase` (piloto fake)
 * quanto `CapturePluginLeadUseCase` (plugin real) reusam este mesmo use
 * case, sem duplicar a lógica de limiares/gravação.
 */
export class EnqueueLeadUseCase {
  constructor(private readonly repository: ILeadQueueRepository) {}

  async execute(
    session: DeviceSession,
    input: LeadCaptureInput,
    consentTextVersion: number
  ): Promise<EnqueueLeadResult> {
    let currentCount: number;
    try {
      currentCount = await this.repository.count();
    } catch (error) {
      this.logStorageError('contagem da fila', error);
      return { outcome: 'storage-unavailable' };
    }

    if (currentCount >= QUEUE_HARD_LIMIT) {
      // CA-09: "a métrica é registrada em logs de erro para revisão do
      // Admin" — o bloqueio é 100% local (nenhuma tentativa de rede chega
      // a ocorrer), então o único lugar possível para essa métrica é este
      // log estruturado no cliente.
      console.error('[EnqueueLeadUseCase] Fila cheia (10.000 leads) — criação bloqueada (CA-09).', {
        eventId: session.eventId,
        deviceId: session.deviceId,
        queueSize: currentCount,
      });
      return { outcome: 'queue-full' };
    }

    const lead = createQueuedLead(session, input, consentTextVersion);

    try {
      const queueSize = await this.repository.add(lead);
      return { outcome: 'queued', lead, queueSize, showQueueAlert: queueSize === QUEUE_ALERT_THRESHOLD };
    } catch (error) {
      this.logStorageError('gravação do lead', error);
      return { outcome: 'storage-unavailable' };
    }
  }

  /** CA-03: erro estruturado em `console.error`, Error level, sem
   * exposição em UI — mesmo destino do CA-02 (ver `SyncLeadQueueUseCase`).
   * Usa o `console` global (não `window.console`) porque este módulo é
   * `application` puro e não deve presumir um objeto `window`. */
  private logStorageError(operation: string, error: unknown): void {
    console.error('[EnqueueLeadUseCase] IndexedDB indisponível — POST cancelado (CA-03).', {
      operation,
      error: error instanceof Error ? error.message : String(error),
    });
  }
}
